import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
  exportVisual
} from '@progress/kendo-react-charts';
import { min, max, mean } from 'lodash';
import { Col, Container, Row } from 'react-bootstrap';
import { PDFExport } from '@progress/kendo-react-pdf';
import { exportImage } from '@progress/kendo-drawing';
import { useInternationalization } from '@progress/kendo-react-intl';

import { Purpose } from './purpose';
import { Competency } from './competency';
import { MeasurableItem } from './measurable-item';
import { ActionPlanItem } from './action-plan-item';
import { Assessment } from '../../types/assessment';
import '../../styles/components/assessment-report/font.scss';
import { AssessmentTeam } from '../../types/assessment-team';
import { AssessmentState } from '../../types/assessment-state';
import { GetResourceValue } from '../../features/assessment/assessment-document';

export interface BoxChartDataItem {
  id: string;
  lower?: number;
  q1?: number;
  median?: number;
  q3?: number;
  upper?: number;
  mean?: number;
}

export interface ChartDataItem {
  id: string;
  score: number;
  teamScore?: number;
  defaultScore: number;
}

interface AssessmentReportProps {
  document: Assessment;
  state: AssessmentState;
  teams: AssessmentTeam[];
  pdfExportRef: React.RefObject<PDFExport | null>;
}

export const AssessmentReport: React.FC<AssessmentReportProps> = ({
  teams,
  state,
  document,
  pdfExportRef
}) => {
  const intl = useInternationalization();

  // get all accepted actions from state.
  const acceptedActions = state.actions
    ? state.actions.filter((i) => i.status == 'Accepted')
    : [];

  // get the IDs of all outcomes, that have accepted actions.
  const acceptedOutcomeIds = [
    ...new Set(acceptedActions.map((item) => item.outcomeId))
  ];

  // get the actual outcomes, that have accepted actions.
  const acceptedOutcomes = (document.assessmentOutcomes ?? []).filter((i) =>
    acceptedOutcomeIds.includes(i.id)
  );

  // get all declined actions from state.
  const declinedActions = state.actions
    ? state.actions.filter((i) => i.status == 'Declined')
    : [];

  // get the IDs of all outcomes, that have declined actions.
  const declinedOutcomeIds = [
    ...new Set(declinedActions.map((item) => item.outcomeId))
  ];

  // get the actual outcomes, that have declined actions.
  const declinedOutcomes = (document.assessmentOutcomes ?? []).filter((i) =>
    declinedOutcomeIds.includes(i.id)
  );

  const totalCompetencies = (document.assessmentOutcomes ?? []).length;
  const totalPlayers = state.players?.length ?? 0;
  const totalActions = state.actions?.length ?? 0;
  const totalAcceptedActions = acceptedActions?.length ?? 0;

  const processRadarChartTeamData = useCallback(
    (teamName: string) => {
      const team = teams.find((team) => team.teamName === teamName);
      if (team) {
        const teamPlayers = team.teamPlayers?.map((player) => player.code);
        const outcomeTeamResult: { id: string; teamAverage: number }[] = [];

        state.outcomes?.forEach((outcome) => {
          const players = outcome.responses.filter((resp) =>
            teamPlayers?.includes(resp.playerCode)
          );
          outcomeTeamResult.push({
            id: outcome.id,
            teamAverage: Number(
              (
                players.reduce((a, b) => a + b.score, 0) / players.length
              ).toFixed(1)
            )
          });
        });

        return outcomeTeamResult;
      }

      return [];
    },
    [teams]
  );

  const radarChartData = useMemo<ChartDataItem[]>(
    () =>
      state.outcomes?.map((item) => {
        const titleResId = document.assessmentOutcomes?.find(
          (o) => o.id === item.id
        ).titleResId;
        const resultData: ChartDataItem = {
          id: GetResourceValue(document, titleResId),
          score: item.averageScore,
          defaultScore: 10
        };
        return resultData;
      }) ?? [],
    [state]
  );

  const radarChartTeamData = useMemo(() => {
    if (!teams.length) return [];

    const result: ChartDataItem[][] = [];
    teams.forEach((team) => {
      if (team.teamName) {
        const teamData = processRadarChartTeamData(team.teamName);
        result.push(
          state.outcomes?.map((item) => {
            const titleResId = document.assessmentOutcomes?.find(
              (o) => o.id === item.id
            ).titleResId;
            const resultData: ChartDataItem = {
              id: GetResourceValue(document, titleResId),
              score:
                teamData.find((score) => score.id === item.id)?.teamAverage ||
                0,
              defaultScore: 10
            };
            return resultData;
          }) || []
        );
      }
    });
    return result;
  }, [teams, state, processRadarChartTeamData]);

  const processBoxPlotChartTeamData = useCallback(
    (teamName: string) => {
      const team = teams.find((team) => team.teamName === teamName);
      if (team) {
        const teamPlayers = team.teamPlayers?.map((player) => player.code);
        const outcomeTeamResult: BoxChartDataItem[] = [];

        state.outcomes?.forEach((outcome) => {
          const scoreList = outcome.responses
            .filter((resp) => teamPlayers?.includes(resp.playerCode))
            .map((resp) => resp.score);
          const count = scoreList.length;

          let q1 = 0;
          let median = 0;
          let q3 = 0;

          if (count > 0) {
            median = scoreList.at(Math.floor(count / 2)) || 0;
            q1 = scoreList.at(Math.floor(count / 4)) || 0;
            q3 = scoreList.at(Math.floor((3 * count) / 4)) || 0;
          }

          const lower = min(scoreList);
          const upper = max(scoreList);
          const average = mean(scoreList);

          const titleResId = document.assessmentOutcomes?.find(
            (o) => o.id === outcome.id
          ).titleResId;

          outcomeTeamResult.push({
            id: GetResourceValue(document, titleResId),
            lower,
            q1,
            median,
            q3,
            upper,
            mean: average
          });
        });

        return outcomeTeamResult;
      }

      return [];
    },
    [state.outcomes, teams, document]
  );

  const boxPlotChartData = useMemo<BoxChartDataItem[]>(
    () =>
      state.outcomes?.map((item) => {
        const titleResId = document.assessmentOutcomes?.find(
          (o) => o.id === item.id
        ).titleResId;
        const resultData: BoxChartDataItem = {
          id: GetResourceValue(document, titleResId),
          ...item.quartiles
        };
        return resultData;
      }) ?? [],
    [state, document]
  );

  const boxPlotChartTeamData = useMemo(() => {
    if (!teams.length) return [];

    const result: BoxChartDataItem[][] = [];
    teams.forEach((team) => {
      if (team.teamName) {
        const teamData = processBoxPlotChartTeamData(team.teamName);
        result.push(teamData);
      }
    });
    return result;
  }, [teams, processBoxPlotChartTeamData]);

  const [radarChartImage, setRadarChartImage] = useState<string[]>([]);
  const radarChart = useRef<Chart[]>([]);
  const [boxChartImage, setBoxChartImage] = useState<string[]>([]);
  const boxChart = useRef<Chart[]>([]);

  useEffect(() => {
    // create timeout because we need to wait the chart perfectly rendered
    const timeoutId = setTimeout(() => {
      const radarChartVisual = radarChart.current.map((radar) =>
        exportVisual(radar)
      );
      radarChartVisual.forEach((radar) => {
        if (radar) {
          exportImage(radar).then((dataURI) =>
            setRadarChartImage((prev) => [...prev, dataURI])
          );
        }
      });

      const boxChartVisual = boxChart.current.map((box) => exportVisual(box));
      boxChartVisual.forEach((box) => {
        if (box) {
          exportImage(box).then((dataURI) =>
            setBoxChartImage((prev) => [...prev, dataURI])
          );
        }
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [radarChart.current, boxChart.current]);

  return (
    <div
      data-bs-theme={'light'}
      className={'text-black'}
      style={{
        position: 'absolute',
        left: '-1000000px',
        top: 0
      }}>
      <PDFExport
        ref={pdfExportRef}
        paperSize={'A4'}
        scale={0.6}
        margin={20}
        fileName={`${state.gameCode}-${new Date().getFullYear()}-assessment`}
        forcePageBreak={'.page-break'}>
        <Container fluid={true} className={'pb-5 pdf-content'}>
          <Row>
            <Col xs={12} className={'d-flex align-items-center'}>
              {document.logoUrl && (
                <div className={'me-3'}>
                  <img
                    src={document.logoUrl}
                    alt={''}
                    className={'img-thumbnail'}
                    style={{ height: '5rem' }}
                  />
                </div>
              )}
              <div>
                <h1>{GetResourceValue(document, document.titleResId ?? '')}</h1>
                {intl.formatDate(new Date(), 'D')}
              </div>
            </Col>
          </Row>
          <Row className={'mt-4'}>
            <Col>
              <Purpose
                title={'Purpose statement'}
                description={GetResourceValue(
                  document,
                  document.descriptionResId ?? ''
                )}
              />
            </Col>
          </Row>
          <Row className={'mt-4'}>
            <Col xs={12}>
              <h2>Summary</h2>
            </Col>
            <Col xs={12}>
              <div className={'counter'}>
                <div className={'counter__item'}>
                  <div className={'item__total'}>
                    <div className={'item__count'}>{totalCompetencies}</div>
                  </div>
                  <div className={'item__label'}>Competencies</div>
                </div>
                <div className={'counter__item'}>
                  <div className={'item__total'}>
                    <div className={'item__count'}>{totalPlayers}</div>
                  </div>
                  <div className={'item__label'}>Participants</div>
                </div>
                <div className={'counter__item'}>
                  <div className={'item__total'}>
                    <div className={'item__count'}>{totalActions}</div>
                  </div>
                  <div className={'item__label'}>Proposed actions</div>
                </div>
                <div className={'counter__item'}>
                  <div className={'item__total'}>
                    <div className={'item__count'}>{totalAcceptedActions}</div>
                  </div>
                  <div className={'item__label'}>Accepted actions</div>
                </div>
              </div>
            </Col>
          </Row>
          <Row className={'mt-4'}>
            <Col xs={12} md={6}>
              <img src={radarChartImage[0]} width={'100%'} alt={''} />
            </Col>
            <Col xs={12} md={6}>
              <img src={boxChartImage[0]} width={'100%'} alt={''} />
            </Col>
            <Col xs={12}>
              <p>
                <small>
                  <em>
                    The radar chart shows the mean (average) of all participants
                    ratings.
                  </em>
                </small>
              </p>
              <p>
                <small>
                  <em>
                    The box plot shows the distribution of ratings submitted by
                    participants through quartiles, highlighting the median
                    (line inside the box), the interquartile range (box), and
                    the spread of the data (whiskers). When no bar is shown, it
                    indicates that participants responses were aligned.
                  </em>
                </small>
              </p>
            </Col>
          </Row>
          <Row className={'mt-4'}>
            <Col xs={12} className="page-break">
              <h2>Accepted actions</h2>
            </Col>
            {acceptedOutcomes.map((assessmentOutcome) => {
              const outcomeState = state?.outcomes?.find(
                (o) => o.id === assessmentOutcome.id
              );
              return (
                <Col xs={12} key={`am-${assessmentOutcome.id}`}>
                  <MeasurableItem
                    document={document}
                    state={state}
                    averageScore={outcomeState?.averageScore}
                    responses={
                      state?.outcomes?.find(
                        (o) => o.id === assessmentOutcome.id
                      )?.responses ?? []
                    }
                    actions={
                      acceptedActions.filter(
                        (a) => a.outcomeId == assessmentOutcome.id
                      ) ?? []
                    }
                    {...assessmentOutcome}
                  />
                </Col>
              );
            })}
          </Row>
          <Row className={'mt-4'}>
            <Col xs={12} className="page-break">
              <h2>Declined actions</h2>
            </Col>
            {declinedOutcomes.map((assessmentOutcome) => {
              const outcomeState = state?.outcomes?.find(
                (o) => o.id === assessmentOutcome.id
              );
              return (
                <Col xs={12} key={`dm-${assessmentOutcome.id}`}>
                  <ActionPlanItem
                    document={document}
                    state={state}
                    averageScore={outcomeState?.averageScore}
                    responses={
                      state?.outcomes?.find(
                        (o) => o.id === assessmentOutcome.id
                      )?.responses ?? []
                    }
                    actions={
                      declinedActions.filter(
                        (a) => a.outcomeId == assessmentOutcome.id
                      ) ?? []
                    }
                    {...assessmentOutcome}
                  />
                </Col>
              );
            })}
          </Row>
          <Row className={'mt-4'}>
            <Col xs={12} className="page-break">
              <h2>Competencies</h2>
            </Col>
            {(document.assessmentOutcomes ?? []).map((assessmentOutcome) => (
              <Col xs={12} key={assessmentOutcome.id}>
                <Competency
                  className={'mt-3'}
                  title={GetResourceValue(
                    document,
                    assessmentOutcome.titleResId ?? ''
                  )}
                  description={GetResourceValue(
                    document,
                    assessmentOutcome.descriptionResId ?? ''
                  )}
                />
              </Col>
            ))}
          </Row>

          {/* team results */}
          {teams.length > 0 &&
            teams.map((team, idx) => (
              <Fragment key={team.teamName}>
                <Row className={'mt-4 page-break'}>
                  <Col>
                    <Purpose
                      title={'Purpose statement'}
                      description={GetResourceValue(
                        document,
                        document.descriptionResId ?? ''
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className={'mt-4'}>
                    <h1>{team.teamName}</h1>
                  </Col>
                </Row>
                <Row className={'mt-4'}>
                  <Col xs={12} md={6}>
                    <img
                      src={radarChartImage[idx + 1]}
                      width={'100%'}
                      alt={''}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <img src={boxChartImage[idx + 1]} width={'100%'} alt={''} />
                  </Col>
                  <Col xs={12}>
                    <p>
                      <small>
                        <em>
                          The radar chart shows the mean (average) of all team
                          members ratings.
                        </em>
                      </small>
                    </p>
                    <p>
                      <small>
                        <em>
                          The box plot shows the distribution of ratings
                          submitted by team members through quartiles,
                          highlighting the median (line inside the box), the
                          interquartile range (box), and the spread of the data
                          (whiskers). When no bar is shown, it indicates that
                          team members responses were aligned.
                        </em>
                      </small>
                    </p>
                  </Col>
                </Row>

                <Row className={'mt-4'}>
                  <Col xs={12} className="page-break">
                    <h2>Competencies</h2>
                  </Col>
                  {(document.assessmentOutcomes ?? []).map(
                    (assessmentOutcome) => (
                      <Col xs={12} key={assessmentOutcome.id}>
                        <Competency
                          className={'mt-3'}
                          title={GetResourceValue(
                            document,
                            assessmentOutcome.titleResId ?? ''
                          )}
                          description={GetResourceValue(
                            document,
                            assessmentOutcome.descriptionResId ?? ''
                          )}
                        />
                      </Col>
                    )
                  )}
                </Row>
              </Fragment>
            ))}
        </Container>
      </PDFExport>

      <Container fluid={true} className={'pb-5 d-none'}>
        <Row className={'mt-4'}>
          <Col xs={12} md={6}>
            {[radarChartData, ...radarChartTeamData].map((radar, idx) => (
              <Chart
                key={idx}
                transitions={false}
                ref={(el) => {
                  el && (radarChart.current[idx] = el);
                }}>
                <ChartSeries>
                  <ChartSeriesItem
                    type={'radarLine'}
                    data={radar}
                    field={'defaultScore'}
                    categoryField={'id'}
                    labels={{
                      visible: true,
                      position: 'center',
                      color: 'var(--bs-primary)',
                      background: 'transparent',
                      font: `bold 0.75rem system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`
                    }}
                    color={'none'}
                    border={{ width: 20 }}
                  />
                  <ChartSeriesItem
                    type={'radarLine'}
                    data={radar}
                    field={'score'}
                    categoryField={'id'}
                    labels={{
                      visible: true,
                      position: 'center',
                      background: '#4698cb',
                      color: '#000',
                      font: `bold 1rem system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`
                    }}
                    color={'#4698cb'}
                  />
                </ChartSeries>
                <ChartValueAxis>
                  <ChartValueAxisItem
                    labels={{ visible: false }}
                    majorGridLines={{ color: '#e9ecef' }}
                    line={{ color: '#e9ecef' }}
                    majorTicks={{ visible: false }}
                    minorTicks={{ visible: false }}
                    majorUnit={1}
                    min={1}
                    max={10}
                  />
                </ChartValueAxis>
                <ChartCategoryAxis>
                  <ChartCategoryAxisItem
                    majorGridLines={{ color: '#e9ecef' }}
                    labels={{ color: '#000' }}
                  />
                </ChartCategoryAxis>
              </Chart>
            ))}
          </Col>
          <Col xs={12} md={6}>
            {[boxPlotChartData, ...boxPlotChartTeamData].map((boxPlot, idx) => (
              <Chart
                key={idx}
                transitions={false}
                ref={(el) => {
                  el && (boxChart.current[idx] = el);
                }}>
                <ChartSeries>
                  <ChartSeriesItem
                    type={'verticalBoxPlot'}
                    lowerField={'lower'}
                    q1Field={'q1'}
                    medianField={'median'}
                    q3Field={'q3'}
                    upperField={'upper'}
                    meanField={'mean'}
                    data={boxPlot}
                    categoryField={'id'}
                    labels={{
                      visible: true,
                      position: 'center',
                      background: '#4698cb',
                      color: '#000',
                      font: `bold 1rem system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`
                    }}
                    color={'#4698cb'}
                  />
                </ChartSeries>
                <ChartValueAxis>
                  <ChartValueAxisItem
                    majorGridLines={{ color: '#e9ecef' }}
                    line={{ color: '#e9ecef' }}
                    majorTicks={{ visible: false }}
                    minorTicks={{ visible: false }}
                    majorUnit={1}
                    labels={{
                      visible: true,
                      color: 'var(--bs-emphasis-color)',
                      font: `1rem var(--bs-body-font-family)`
                    }}
                    min={1}
                    max={10}
                  />
                </ChartValueAxis>
                <ChartCategoryAxis>
                  <ChartCategoryAxisItem
                    majorGridLines={{ color: '#e9ecef' }}
                    labels={{ color: '#000' }}
                  />
                </ChartCategoryAxis>
              </Chart>
            ))}
          </Col>
        </Row>
      </Container>
    </div>
  );
};
